import { apiFetchData } from '../helpers/api';
import { clearGarbageAuthData } from '../helpers/clearData';
import { addMetaTag } from '../helpers/DOM';
import { getLangCountryPrefix } from '../helpers/utils';

export class UpdateReduxService {
    #lang;
    #togglePopup;
    #toggleModal;
    #toggleSpinner;
    #setCheque;
    #setExtra;
    #setHistory;
    #updateCurrentSiteSection;
    #setNewForm;
    #initCalculator;
    #updateAuth;
    #updateCards;
    #updateMessages;
    #updateAuthPromoBlock;
    #updatePageMessages;
    #updateJsMessages;
    #stateOptions;
    #chatBlockStatus;

    constructor(lang) {
        this.#lang = lang;
    }

    setMessageAction(updateJsMessages, updatePageMessages) {
        if (this.#updateJsMessages === undefined) {
            this.#updateJsMessages = updateJsMessages;
            this.#updatePageMessages = updatePageMessages;
        }
    }

    setChatAction(chatBlockStatus) {
        if (this.#chatBlockStatus === undefined) {
            this.#chatBlockStatus = chatBlockStatus;
        }
    }

    setHistoryAction(setCheque, setHistory) {
        if (this.#setCheque === undefined) {
            this.#setCheque = setCheque;
        }
        if (this.#setHistory === undefined) {
            this.#setHistory = setHistory;
        }
    }

    setAuthAction(updateAuth) {
        if (this.#updateAuth === undefined) {
            this.#updateAuth = updateAuth;
        }
    }

    setFormAction(setNewForm) {
        if (this.#setNewForm === undefined) {
            this.#setNewForm = setNewForm;
        }
    }

    setCalculatorAction(initCalculator) {
        if (this.#initCalculator === undefined) {
            this.#initCalculator = initCalculator;
        }
    }

    setAppAction(toggleModal, togglePopup, toggleSpinner, updateAuthPromoBlock, updateCurrentSiteSection, updateMessages) {
        if (this.#toggleModal === undefined) {
            this.#toggleModal = toggleModal;
            this.#togglePopup = togglePopup;
            this.#toggleSpinner = toggleSpinner;
            this.#updateAuthPromoBlock = updateAuthPromoBlock;
            this.#updateCurrentSiteSection = updateCurrentSiteSection;
            this.#updateMessages = updateMessages;
        }
    }

    setExtraAction(setExtra) {
        if (this.#setExtra === undefined) {
            this.#setExtra = setExtra;
        }
    }

    setCardsAction(updateCards) {
        if (this.#updateCards === undefined) {
            this.#updateCards = updateCards;
        }
    }

    setStateOptions(stateOptions) {
        this.#stateOptions = stateOptions;
    }

    fetchData(lang, isAuthZone, country) {
        this.#togglePopup(false);
        this.#toggleModal(false);
        this.#toggleSpinner(true);
        const apiUrl = this.#getUrl(lang, country);
        let isToggleSpinner = true;
        return apiFetchData(apiUrl)
            .then((json) => {
                if (json.redirectTo) {
                    isToggleSpinner = false;
                    window.location.href = json.redirectTo;
                    return Promise.resolve();
                }
                this.#updateDocument(json);
                return this.#updateRedux(json);
            })
            .finally(() => {
                if (isToggleSpinner) {
                    this.#toggleSpinner(false);
                }
            });
    }

    #getUrl(lang, country) {
        let apiUrl = `${window.location.pathname}${window.location.search}`;
        if (lang && this.#lang && lang !== this.#lang) {
            if (apiUrl.indexOf(getLangCountryPrefix(this.#lang, country)) !== -1) {
                apiUrl = apiUrl.replace(getLangCountryPrefix(this.#lang, country), getLangCountryPrefix(lang, country));
            } else {
                apiUrl = apiUrl + (apiUrl.indexOf('?') === -1 ? '?' : '&') + 'lang=' + lang;
            }
            this.#lang = lang;
        }
        return apiUrl;
    }

    #updateDocument = (json = {}) => {
        document.title = json.pageTitle;
        const description = document.querySelector('meta[name="description"]');
        if (description) {
            description.setAttribute('content', json.pageDescription);
        } else if (json.pageDescription) {
            addMetaTag('description', json.pageDescription);
        }
        const keywords = document.querySelector('meta[name="keywords"]');
        if (keywords) {
            keywords.setAttribute('content', json.pageKeywords);
        } else if (json.pageKeywords) {
            addMetaTag('keywords', json.pageKeywords);
        }
    };

    #updateRedux(json = {}) {
        const promises = [];
        if (json.messages && this.#updatePageMessages) {
            promises.push(this.#updatePageMessages(json.messages));
        }
        if (json.cheque && this.#setCheque) {
            promises.push(this.#setCheque(json.cheque));
        }
        if (this.#chatBlockStatus) {
            promises.push(this.#chatBlockStatus(json.chatBlocked));
        }
        if (json.currentSiteSection && this.#updateCurrentSiteSection) {
            promises.push(this.#updateCurrentSiteSection(json.currentSiteSection));
        }
        if (json.forms && this.#setNewForm) {
            Object.values(json.forms).forEach((form) => {
                promises.push(this.#setNewForm(form.name, form.fields, form.action));
            });
        }
        if (json.paymentForm && this.#initCalculator) {
            promises.push(this.#initCalculator(json));
        }
        if (json.auth && this.#updateAuth) {
            promises.push(this.#updateAuth(json?.auth).then(() => clearGarbageAuthData(json)));
        }
        if (json.cards && this.#updateCards) {
            promises.push(this.#updateCards(json));
        }
        if (json.messageBodyMessages && this.#updateMessages) {
            promises.push(this.#updateMessages(json.messageBodyMessages));
        }
        if (json.authPromoAction && this.#updateAuthPromoBlock) {
            promises.push(this.#updateAuthPromoBlock(json.authPromoAction));
        }
        if (json.extra && this.#setExtra) {
            promises.push(this.#setExtra(json.extra));
        }
        if ((json.extra?.paymentHistory || json.extra?.billHistory) && this.#setHistory) {
            promises.push(this.#setHistory(json.extra));
        }
        return Promise.all(promises).then(() => json);
    }
}
